// The Elsewhere search, widened from one substrate to three.
//
// PR #1219 found counterparties from RAW OS TELEMETRY alone, because that was
// the only substrate where both sides of an agent-to-agent file exchange were
// visible at once. It still is the only one that sees a process that never
// tells the prompt stream what it opened — but it is blind in its own ways:
// an Anthropic agent's Write emits no FileState, and OS capture on a quiet
// endpoint can miss a fast touch entirely. Two more substrates exist now:
//
//   - TOOL CALLS: the enriched prompt rows whose `resource_paths` name the
//     file. Finds the agent that worked the file through its own tools, and
//     each hit is a TURN — an openable trace id — not just a session.
//   - CONVERSATION CONTENT: the normalized conversation model's search view,
//     which indexes message text, tool results, and (since 2026-08-12)
//     assistant RESPONSES. Finds the agent whose only visible relationship to
//     the file is having read or said its content — including the response
//     half of a conversation that no request body ever carried.
//
// A counterparty seen by two substrates is a stronger finding than one seen by
// one, and a counterparty seen by ONLY the new substrates is a real participant
// the OS search would have dropped. So the merge keeps every session any
// channel found, and keeps the per-channel evidence separate — the reader gets
// "read 12× (OS) · named in 4 turns · 7 content mentions", never a blended
// count that can't be traced back to what was actually observed.

// Bounded evidence samples per channel, mirroring COUNTERPARTY_EVENT_SAMPLE on
// the OS side: a summary the reader cannot open is not a reading.
const MENTION_TURN_SAMPLE = 12;
const CONTENT_SAMPLE = 12;

// Fold prompt-side rows (one per prompt event naming the file) into one entry
// per session. Each hit carries its own trace id, so the calls channel is the
// only one whose evidence is directly openable without resolution.
export function foldFileMentions(rows = []) {
  const byId = new Map();
  for (const r of rows || []) {
    const id = r?.session_id;
    if (!id) continue;
    let it = byId.get(id);
    if (!it) {
      it = {
        session_id: id, endpoint_id: r.endpoint_id ?? null,
        agent_type: r.agent_type ?? null, provider: r.provider ?? null, model: r.model ?? null,
        count: 0, turns: [], first_seen: null, last_seen: null,
      };
      byId.set(id, it);
    }
    it.count += 1;
    if (it.turns.length < MENTION_TURN_SAMPLE && r.trace_id) {
      it.turns.push({ trace_id: r.trace_id, at: r.at ?? null });
    }
    // Labels ride the same rows; a session's later prompts may carry them when
    // the first did not.
    if (!it.agent_type && r.agent_type) it.agent_type = r.agent_type;
    if (!it.provider && r.provider) it.provider = r.provider;
    if (!it.model && r.model) it.model = r.model;
    const t = r.at ?? null;
    if (t && (it.first_seen == null || t < it.first_seen)) it.first_seen = t;
    if (t && (it.last_seen == null || t > it.last_seen)) it.last_seen = t;
  }
  return [...byId.values()];
}

// A file-name mention only counts when some occurrence of the name is WHOLE
// on both edges. The search runs `LIKE %<basename>%`, and LIKE matches
// substrings: a focal `b2.md` matched `prompt-b2.md` in another conversation
// — a different file entirely, presented as a counterparty (found live) —
// and the same collision exists on the right edge (`b2.mdx`, `b2.md5`). A
// hyphen, dot, or word character touching EITHER end of the match means it
// is part of a longer name; a separator, quote, whitespace, or the text's
// edge on both sides means it is the file. A snippet that clipped the term
// out entirely cannot disprove the match, so it keeps the row —
// under-claiming here would hide real participants to avoid a cosmetic risk.
const NAME_SUFFIX_JOINT_RE = /[A-Za-z0-9_.-]/;

// The one spelling of "the file's name" every channel shares — the search
// spec's LIKE term and the fold's boundary check must derive it identically
// or the guard tests a different name than the query matched.
export function fileBasenameOf(path) {
  return String(path || "").replace(/\\/g, "/").split("/").filter(Boolean).pop() ?? null;
}

export function contentMentionIsWholeName(snippet, basename) {
  if (!basename) return true;
  const text = String(snippet || "").toLowerCase();
  const name = String(basename).toLowerCase();
  if (!text.includes(name)) return true;
  let i = -1;
  while ((i = text.indexOf(name, i + 1)) !== -1) {
    // Both edges: `prompt-b2.md` collides on the left, `b2.mdx`/`b2.md5`
    // on the right. Whole-name means neither side continues the name.
    const before = i === 0 || !NAME_SUFFIX_JOINT_RE.test(text[i - 1]);
    const end = i + name.length;
    const after = end === text.length || !NAME_SUFFIX_JOINT_RE.test(text[end]);
    if (before && after) return true;
  }
  return false;
}

// Fold conversation-search rows into one entry per session (the conversation
// model's trace_id IS the session's notification id — verified on staging, the
// two are the same UUID). `responses` is counted separately because an
// assistant-role mention is the evidence this channel uniquely holds: content
// that never appeared in any captured request body.
export function foldContentMentions(rows = [], { basename = null } = {}) {
  const byId = new Map();
  for (const r of rows || []) {
    const id = r?.session_id;
    if (!id) continue;
    if (!contentMentionIsWholeName(r?.snippet, basename)) continue;
    let it = byId.get(id);
    if (!it) {
      it = {
        session_id: id, endpoint_id: r.endpoint_id ?? null,
        total: 0, responses: 0, byKind: {}, samples: [],
        first_seen: null, last_seen: null,
      };
      byId.set(id, it);
    }
    it.total += 1;
    const role = String(r.role || "");
    const kind = String(r.part_kind || r.kind || "");
    if (role === "assistant") it.responses += 1;
    const key = [role || "?", kind || "?"].join(" ");
    it.byKind[key] = (it.byKind[key] || 0) + 1;
    if (it.samples.length < CONTENT_SAMPLE) {
      it.samples.push({ at: r.at ?? null, role, kind, snippet: r.snippet ?? "" });
    }
    const t = r.at ?? null;
    if (t && (it.first_seen == null || t < it.first_seen)) it.first_seen = t;
    if (t && (it.last_seen == null || t > it.last_seen)) it.last_seen = t;
  }
  return [...byId.values()];
}

// Merge the three channel folds into one row per session.
//
// The OS rows arrive AFTER host-process resolution, so a Codex conversation
// that was resolved out of its shared app-server id merges with the calls and
// content evidence recorded against the conversation directly — that
// cross-channel agreement is the point of resolving first.
//
// The focal session is SPLIT OUT rather than dropped: its evidence is the
// other half of "both sides of the exchange", and the panel wants to show it
// next to the counterparties, not pretend the focal agent wasn't there.
export function mergeCounterpartyChannels({ os = [], calls = [], content = [], focalSessionId = null } = {}) {
  const byId = new Map();
  const ensure = (id, endpointId) => {
    let it = byId.get(id);
    if (!it) {
      it = {
        session_id: id, endpoint_id: endpointId ?? null,
        writes: 0, reads: 0, commands: 0,
        first_seen: null, last_seen: null, events: [],
        channels: {},
      };
      byId.set(id, it);
    }
    if (!it.endpoint_id && endpointId) it.endpoint_id = endpointId;
    return it;
  };
  const widen = (it, first, last) => {
    if (first && (it.first_seen == null || first < it.first_seen)) it.first_seen = first;
    if (last && (it.last_seen == null || last > it.last_seen)) it.last_seen = last;
  };

  for (const row of os || []) {
    if (!row?.session_id) continue;
    const it = ensure(row.session_id, row.endpoint_id);
    // The OS row IS the base shape (writes/reads/commands/events plus any
    // host-resolution fields) — carry it whole so nothing the resolver
    // attached (host_session_id, thread_id, trace_id) is lost.
    Object.assign(it, row, { channels: it.channels });
    it.channels.os = {
      writes: row.writes || 0, reads: row.reads || 0, commands: row.commands || 0,
      first_seen: row.first_seen ?? null, last_seen: row.last_seen ?? null,
    };
  }
  for (const m of calls || []) {
    if (!m?.session_id) continue;
    const it = ensure(m.session_id, m.endpoint_id);
    it.channels.calls = m;
    // A turn the prompt side names is an openable link; prefer it only when the
    // OS side did not already resolve one.
    if (!it.trace_id && m.turns?.length) {
      it.trace_id = m.turns[0].trace_id;
      it.trace_at = m.turns[0].at ?? null;
    }
    if (!it.agent_type && m.agent_type) it.agent_type = m.agent_type;
    if (!it.provider && m.provider) it.provider = m.provider;
    if (!it.model && m.model) it.model = m.model;
    widen(it, m.first_seen, m.last_seen);
  }
  for (const c of content || []) {
    if (!c?.session_id) continue;
    const it = ensure(c.session_id, c.endpoint_id);
    it.channels.content = c;
    widen(it, c.first_seen, c.last_seen);
  }

  let focal = null;
  if (focalSessionId && byId.has(focalSessionId)) {
    focal = byId.get(focalSessionId);
    byId.delete(focalSessionId);
  }
  const involvement = (r) =>
    (r.reads || 0) + (r.commands || 0)
    + (r.channels.calls?.count || 0)
    + (r.channels.content?.total || 0);
  const rows = [...byId.values()].sort((a, b) =>
    ((b.writes || 0) - (a.writes || 0))
    || (involvement(b) - involvement(a))
    || String(a.session_id).localeCompare(String(b.session_id)));
  return { focal, rows };
}

// Attach probe-match results to merged rows. Probes are a separate, second
// search (the reader asks for it), so this is a patch over rows that already
// exist — a session matched by a probe but absent from the channel rows is
// still surfaced, because sharing the file's CONTENT without ever touching the
// file by name is exactly the finding the probe search exists for.
export function attachProbeMatches(rows = [], probeFold = []) {
  const byId = new Map((probeFold || []).map((p) => [p.session_id, p]));
  const out = (rows || []).map((r) => {
    const p = byId.get(r.session_id);
    if (p) byId.delete(r.session_id);
    return p ? { ...r, probes: p } : r;
  });
  for (const p of byId.values()) {
    out.push({
      session_id: p.session_id, endpoint_id: p.endpoint_id ?? null,
      writes: 0, reads: 0, commands: 0,
      first_seen: p.first_seen ?? null, last_seen: p.last_seen ?? null,
      events: [], channels: {}, probes: p,
    });
  }
  return out;
}

// A probe that matches this many DISTINCT sessions is not finding an exchange
// any more — it is finding substrate everyone shares (a license header, a
// harness banner, a vendored constant). Same rule as a document-frequency cap
// in a content index: the probe stays a valid search, but its matches stop
// being evidence that two particular agents exchanged anything.
export const PROBE_SESSION_CAP = 12;

export function probeIsDiffuse(result) {
  if (!result || result.ok === false) return false;
  // The fingerprint index answers "too common" by document frequency without
  // expanding the carriers, so the verdict arrives as a flag, not as rows.
  if (result.diffuse === true) return true;
  const sessions = new Set();
  for (const r of result.rows || []) {
    if (r?.session_id) sessions.add(r.session_id);
    if (sessions.size > PROBE_SESSION_CAP) return true;
  }
  return false;
}

// The probes whose matches count as exchange evidence, and the ones set aside
// as diffuse. One partition shared by the fold and by the caller's reporting,
// so "matched k of n" and "m probes were too common to count" never disagree.
export function partitionProbeResults(probeResults = []) {
  const asked = (probeResults || []).filter((p) => p && p.ok !== false);
  const counted = [];
  const diffuse = [];
  for (const p of asked) (probeIsDiffuse(p) ? diffuse : counted).push(p);
  return { counted, diffuse };
}

// Fold per-probe search results into one entry per session: how many DISTINCT
// probes matched, out of how many were counted. "2 of 3" is a much stronger
// content-overlap claim than a raw hit count, which one repeated snippet
// could inflate. Diffuse probes are excluded from both sides of that claim.
//
// Each entry also splits its hits by which SIDE of the counterpart carried
// the content: `authored` counts assistant-role hits (the counterpart said or
// produced the content), `received` counts user/tool-role hits (the content
// arrived in its inputs). That split is the direction of the exchange, read
// straight off the rows — rows with no role (raw request bodies) count as
// neither.
export function foldProbeMatches(probeResults = []) {
  const { counted } = partitionProbeResults(probeResults);
  const byId = new Map();
  counted.forEach((p, i) => {
    for (const r of p.rows || []) {
      const id = r?.session_id;
      if (!id) continue;
      let it = byId.get(id);
      if (!it) {
        it = {
          session_id: id, endpoint_id: r.endpoint_id ?? null,
          matchedSet: new Set(), of: counted.length, samples: [],
          authored: 0, received: 0,
          first_seen: null, last_seen: null,
        };
        byId.set(id, it);
      }
      it.matchedSet.add(i);
      const role = String(r.role || "");
      if (role === "assistant") it.authored += 1;
      else if (role === "user" || role === "tool") it.received += 1;
      if (it.samples.length < CONTENT_SAMPLE) {
        it.samples.push({ at: r.at ?? null, role, kind: r.part_kind ?? "", snippet: r.snippet ?? "" });
      }
      const t = r.at ?? null;
      if (t && (it.first_seen == null || t < it.first_seen)) it.first_seen = t;
      if (t && (it.last_seen == null || t > it.last_seen)) it.last_seen = t;
    }
  });
  return [...byId.values()].map(({ matchedSet, ...rest }) => ({ ...rest, matched: matchedSet.size }));
}

// --- Content probes -------------------------------------------------------

// How many probes one search spends, and the line lengths worth probing with.
// Three is enough to say "overlap" vs "coincidence" (2/3 beats any single
// hit), and each probe is its own bounded query, so this number is a query
// budget, not a tuning knob.
export const PROBE_COUNT = 3;
const PROBE_MIN_LEN = 24;
// Long enough to be distinctive, short enough that a probe never straddles a
// search-view chunk boundary (chunks are KBs; this is not).
const PROBE_MAX_LEN = 96;
const PROBE_MIN_ALNUM = 12;

// Lines of the file as reconstructed at a turn, in order. The reconstruction
// (traceFileTree's reconstructFile) returns display rows; the probe source is
// the numbered lines — gaps and deletion tombstones are not the file's
// current content and must not become probes.
export function linesFromReconstruction(rec) {
  return (rec?.rows || [])
    .filter((r) => r && r.no != null && !r.del)
    .map((r) => String(r.text ?? ""));
}

// Pick up to PROBE_COUNT distinctive lines, spread across the file.
//
// Deterministic on purpose: the same file text always yields the same probes,
// so a re-run is a re-ask of the same question, and a test can pin the rule.
// The spread matters more than it looks — a file whose head is boilerplate
// (imports, headers) and whose body is the actual content should not spend all
// three probes on the head, so candidates are split into thirds by position
// and each third contributes its longest line.
export function deriveContentProbes(lines = [], { max = PROBE_COUNT } = {}) {
  const candidates = [];
  (lines || []).forEach((raw, i) => {
    const line = String(raw ?? "").trim();
    if (line.length < PROBE_MIN_LEN) return;
    const alnum = (line.match(/[a-zA-Z0-9]/g) || []).length;
    if (alnum < PROBE_MIN_ALNUM) return;
    candidates.push({ line: line.slice(0, PROBE_MAX_LEN), at: i });
  });
  if (!candidates.length) return [];
  const seen = new Set();
  const probes = [];
  const groups = Math.min(max, candidates.length);
  const per = Math.ceil(candidates.length / groups);
  for (let g = 0; g < groups; g += 1) {
    const slice = candidates.slice(g * per, (g + 1) * per);
    const best = slice
      .filter((c) => !seen.has(c.line))
      .sort((a, b) => (b.line.length - a.line.length) || (a.at - b.at))[0];
    if (!best) continue;
    seen.add(best.line);
    probes.push(best.line);
  }
  return probes;
}

// --- Needle probes ----------------------------------------------------------

// The needle class: unbroken runs of id-shaped characters — commit SHAs,
// session and run ids, tokens, path segments, encoded payloads. Measured
// against labeled cross-agent exchanges on live data, these runs are what
// actually carries a content join: an id recurs verbatim on the counterpart's
// side, while prose gets reflowed, quoted, or clipped in transit. They are
// also the cheapest probes the search view can plan — highly selective single
// terms.
//
// A run is only a needle if its alphabet is diverse: divider art, zeroed ids,
// and repeated-character padding are long runs that identify nothing.
const NEEDLE_RE = /[A-Za-z0-9+/=_-]{24,}/g;
const NEEDLE_MIN_DISTINCT_CHARS = 8;
export const NEEDLE_COUNT = 3;

// The raw extraction, shared by every needle consumer: qualifying runs of one
// text, clipped and diversity-guarded, in order of appearance. Clipping keeps
// a probe a cheap LIKE — either end of a long run is an exact substring of it,
// so the match is unaffected. WHICH end matters for discrimination: paths
// discriminate at their TAIL (the basename), and a head-clip made every file
// under one deep directory collapse to the identical directory-prefix needle
// — one string then joined every counterpart that ever touched the directory,
// and every pin's evidence recursed to the same partial path (found live).
// Slash-bearing runs keep the tail; opaque tokens keep the head.
export function extractNeedleRuns(text) {
  const runs = [];
  for (const run of String(text ?? "").match(NEEDLE_RE) || []) {
    const needle = run.length <= PROBE_MAX_LEN ? run
      : run.includes("/") ? run.slice(-PROBE_MAX_LEN) : run.slice(0, PROBE_MAX_LEN);
    if (new Set(needle).size < NEEDLE_MIN_DISTINCT_CHARS) continue;
    runs.push(needle);
  }
  return runs;
}

export function deriveContentNeedles(lines = [], { max = NEEDLE_COUNT } = {}) {
  const seen = new Set();
  const candidates = [];
  (lines || []).forEach((raw, i) => {
    for (const needle of extractNeedleRuns(raw)) {
      if (seen.has(needle)) continue;
      seen.add(needle);
      candidates.push({ needle, at: i });
    }
  });
  // Longest first: length is the closest cheap proxy for "cannot be a
  // coincidence". Position breaks ties so the same file always yields the
  // same probes.
  return candidates
    .sort((a, b) => (b.needle.length - a.needle.length) || (a.at - b.at))
    .slice(0, max)
    .map((c) => c.needle);
}

// The probe set for one file: needle probes first, line probes to round it
// out. Two classes on purpose — needles catch the id-shaped content that
// survives any amount of quoting, lines catch prose that carries no ids. A
// probe contained in another is dropped: both would match the same rows and
// inflate "k of n" with a correlated ask.
export function deriveFileProbeSet(lines = []) {
  const probes = [...deriveContentNeedles(lines)];
  for (const line of deriveContentProbes(lines)) {
    if (probes.some((p) => p.includes(line) || line.includes(p))) continue;
    probes.push(line);
  }
  return probes;
}
